type Prompt = {
  role: "user" | "system" | "assistant";
  content: string;
};

export const EXAMPLE_PROMPTS: Prompt[] = [
  {
    role: "user",
    content: `We give you some code. Edit the code as per the instructions. The instructions apply to the lines that start with "$". Try to contain the changes to this single file. Try not to add any imports. Remove the "$" selector from the final output. RESPOND ONLY WITH THE CHANGED CODE AND MAKE SURE TO ADD SURROUNDING LINES OF UNCHANGED CODE.

Path: /src/App.tsx
Code:
\`\`\`
function HelloWorld() {
  return (
    <div>
      <div>Test</div>
      <div>Hello world</div>
    </div>
  );
}
\`\`\`

Instructions:
Make it red

`,
  },
  {
    role: "assistant",
    content: `\`\`\`
function HelloWorld() {
  return (
    <div>
      <div>Test</div>
      <div style={{ color: 'red' }}>Hello world</div>
    </div>
  );
}
\`\`\``,
  },
  {
    role: "user",
    content: `Path: /src/Hello.tsx

Code:
\`\`\`
function HelloWorld() {
  return (
    <div>
$      <div>Test</div>
      <div>Hello world</div>
    </div>
  );
}

export default App;

\`\`\`

Instructions:
Make it further away from "Hello world" using tailwind classes

`,
  },
  {
    role: "assistant",
    content: `\`\`\`
function HelloWorld() {
  return (
    <div>
      <div className="mb-4">Test</div>
      <div>Hello world</div>
    </div>
  );
}

export default App;

\`\`\``,
  },
  {
    role: "user",
    content: `Path: /src/Hello.tsx

Code:
\`\`\`
function HelloWorld() {
  return (
    <main>
      <h1>Test</div>
      <div>Hello world</div>
    </main>
  );
}
\`\`\`

Instructions:
Make it bigger
`,
  },
  {
    role: "assistant",
    content: `\`\`\`
function HelloWorld() {
  return (
    <main>
      <h1 style={{ fontSize: 32 }}>Test</div>
      <div>Hello world</div>
    </main>
  );
}
\`\`\``,
  },
  {
    role: "user",
    content: `Path: /app/page.tsx
Code:
\`\`\`
"use client";

export default function Home() {
  return (
    <main className="min-h-screen flex-col items-center p-24">
      <h1>Hello Boxy</h1>
      <h2>Nice change!</h2>
    </main>
  );
}
\`\`\`

Instructions:
Make this a button that counts up

`,
  },
  {
    role: "assistant",
    content: `\`\`\`
"use client";
import { useState } from 'react';

export default function Home() {
  const [count, setCount] = useState(0);

  const handleClick = () => {
    setCount(count + 1);
  }

  return (
    <main className="min-h-screen flex-col items-center p-24">
      <h1>Hello Boxy</h1>
      <button onClick={handleClick}>Count: {count}</button>
    </main>
  );
}
  \`\`\``,
  },
  {
    role: "user",
    content: `Path: /web/codesandbox-v2/src/components/EditorTitle/implementations/RepositoryTitle.tsx
Code:
\`\`\`
      <TitleUI
        type={{ type: "repository", owner: project.owner, repo: project.repo }}
      >
        {/* Repository name */}
        <AnimatedDiv>
          {hasWriteAccess(project.authorization) && !project.contribution ? (
            <Tooltip content={\`Open \${owner}/\${repo} in the dashboard\`}>
              <Button
                as="a"
                css={{
                  padding: 0,
                }}
                href={paths.dashboard.branches(owner, repo, ownerWorkspaceId)}
              >
                {ownerName} / {repo}
              </Button>
            </Tooltip>
          ) : (
            <Text truncate>
              {owner}/{repo}
            </Text>
          )}
        </AnimatedDiv>

        {/* Branch Icon */}
        {project.contribution ? (
          <AnimatedDiv
            css={{
              display: "flex",
              alignItems: "center",
              // Hate this but the contribution branch icon
              // looks off next to small case letters
              marginTop: "1px",
              marginLeft: "1px",
            }}
          >
            <Icon size={4} type={"contribution"} />
          </AnimatedDiv>
        ) : (
\`\`\`

Instructions:
Change the slash to a hyphen

`,
  },
  {
    role: "assistant",
    content: `\`\`\`
          <Tooltip content={\`Open \${owner}/\${repo} in the dashboard\`}>
            <Button
              as="a"
              css={{
                padding: 0,
              }}
              href={paths.dashboard.branches(owner, repo, ownerWorkspaceId)}
            >
              {ownerName} - {repo}
            </Button>
          </Tooltip>
        ) : (
          <Text truncate>
            {owner}-{repo}
          </Text>
        )}
\`\`\``,
  },
  {
    role: "user",
    content: `Path: /web/codesandbox-v2/src/components/PrimaryCTA/PrimaryCTA.tsx
    Code:
    \`\`\`
  const renderDropDownOptions = () => {
    if (canCreateContributionBranch) {
      return <ForkContributionOptions />;
    }

    if (!allowedCapabilities.branch) {
      return null;
    }

    if (isDefaultBranch) {
      return null;
    }

    return <ForkRegularOptions />;
  };

  if (visibility.isInIframe) {
    // We're in an embed mode
    return (
      <Button
        as="a"
        href={document.location.href}
        onClick={() => {
          track("embed_open_editor");
        }}
        rel="noopener"
        target="_blank"
      >
        Open Editor
      </Button>
    );
  }

  if (owner === SANDBOX_OWNER) {
    return <ForkSandboxButton dispatchFork={dispatchFork} fork={fork} />;
  }

  return (
      <RegisterBranchCommands />
\`\`\`

Instructions:
Replace the contents with "Web Editor"

`,
  },
  {
    role: "assistant",
    content: `\`\`\`
  if (visibility.isInIframe) {
    // We're in an embed mode
    return (
      <Button
        as="a"
        href={document.location.href}
        onClick={() => {
          track("embed_open_editor");
        }}
        rel="noopener"
        target="_blank"
      >
        Web Editor
      </Button>
    );
  }
    \`\`\``,
  },
  {
    role: "user",
    content: `Path: /app/page.tsx
Code:
\`\`\`
"use client";

export default function Home() {
  return (
    <main className="min-h-screen flex-col p-12">
      <div>
        <img src="https://fqfqsd-4000.csb.app/imgs/csb-avatar.png" />
        <div>
          <h1>Ives van Hoorne</h1>
          <h2>@compuives</h2>
        </div>
      </div>
    </main>
  );
}
    \`\`\`
    
    Instructions:
    Make it round and black and white
    
`,
  },
  {
    role: "assistant",
    content: `\`\`\`
      <div>
        <img
          style={{
            borderRadius: '50%',
            filter: 'grayscale(100%)'
          }}
          src="https://fqfqsd-4000.csb.app/imgs/csb-avatar.png"
        />
        <div>
          <h1>Ives van Hoorne</h1>
          <h2>@compuives</h2>
        </div>
      </div>
    </main>
  );
}
\`\`\``,
  },
  {
    role: "user",
    content: `Path: /app/page.tsx
Code:
\`\`\`
"use client";

export default function Home() {
  return (
    <main className="min-h-screen flex-col p-12">
      <div>
        <img src="https://fqfqsd-4000.csb.app/imgs/csb-avatar.png" />
        <div>
          <h1>Ives van Hoorne</h1>
          <h2>@compuives</h2>
        </div>
      </div>
    </main>
  );
}
    \`\`\`
    
    Instructions:
    Make it more accessible
    
`,
  },
  {
    role: "assistant",
    content: `\`\`\`
      <div>
        <img
          alt="Ives van Hoorne's avatar"
          src="https://fqfqsd-4000.csb.app/imgs/csb-avatar.png"
        />
        <div>
          <h1>Ives van Hoorne</h1>
          <h2>@compuives</h2>
        </div>
      </div>
    </main>
);
}
\`\`\``,
  },
  {
    role: "user",
    content: `Path: /app/page.tsx
Code:
\`\`\`
"use client";

export default function Home() {
  return (
    <main className="min-h-screen flex-col p-12">
      <div className="p-12 bg-white rounded-md shadow-lg">
        <h1>Hey Boxy!</h1>
      </div>
    </main>
  );
}
\`\`\`

Instructions:
Make it gray
    
`,
  },
  {
    role: "assistant",
    content: `\`\`\`
"use client";

export default function Home() {
  return (
    <main className="min-h-screen flex-col p-12">
      <div className="p-12 bg-white rounded-md shadow-lg">
        <h1 className="text-gray-600">Hey Boxy!</h1>
      </div>
    </main>
  );
}
\`\`\``,
  },
];

export const getPrompt = (
  filePath: string,
  code: string,
  instruction: string,
): Prompt[] => {
  return [
    ...EXAMPLE_PROMPTS,
    {
      role: "user",
      content: `Path: ${filePath}
Code:
\`\`\`
${code}
\`\`\`

Instructions:
${instruction}

`,
    },
  ];
};
